import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DateFormatEnum } from "@models/core/date-format";
import { AccessToken, RefreshToken } from "@models/http/http-options";
import { format } from "date-fns";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class BaseService {
  apiUrl = environment.apiUrl;

  protected readonly router = inject(Router);

  constructor(protected readonly http: HttpClient) {}

  formatDate(date: Date): string {
    return format(date, DateFormatEnum.YearMonthDayAndFullTime);
  }

  post<T, B = unknown>(uri: string, body: B, options?: Record<string, unknown>): Observable<T> {
    const normalizedBody = this.normalBody(body);
    const fullUrl = `${this.apiUrl}${uri}`;

    return this.http.post<T>(fullUrl, normalizedBody, options) as Observable<T>;
  }

  put<T, B = unknown>(uri: string, body: B, options?: Record<string, unknown>): Observable<T> {
    const normalizedBody = this.normalBody(body);
    const fullUrl = `${this.apiUrl}${uri}`;

    return this.http.put<T>(fullUrl, normalizedBody, options) as Observable<T>;
  }

  patch<T, B = unknown>(uri: string, body: B, options?: Record<string, unknown>): Observable<T> {
    const normalizedBody = this.normalBody(body);
    const fullUrl = `${this.apiUrl}${uri}`;

    return this.http.patch<T>(fullUrl, normalizedBody, options) as Observable<T>;
  }

  get<T>(uri: string, params?: Record<string, any>): Observable<T> {
    const normalParams = this.normalBody(params);
    const fullUrl = `${this.apiUrl}${uri}`;
    const urlParams = this.serializeParams(normalParams || {});

    return this.http.get<T>(fullUrl, { params: urlParams });
  }

  serializeParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach((key) => {
      const value = params[key];

      if (value == null || value === "") {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          httpParams = httpParams.append(key, item);
        });
      } else {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  remove<T = any, B = unknown>(uri: string, body?: B): Observable<T> {
    const normalizedBody = this.normalBody(body);
    return this.http.delete<T>(this.apiUrl + uri, { body: normalizedBody }) as Observable<T>;
  }

  normalBody<B>(body: B): B {
    if (body instanceof FormData) {
      return body;
    }

    if (Array.isArray(body)) {
      return body;
    }

    const normalizedBody = body ? { ...(body as Record<string, unknown>) } : {};

    Object.keys(normalizedBody).forEach((key) => {
      if (normalizedBody[key] instanceof Date) {
        normalizedBody[key] = this.formatDate(normalizedBody[key] as Date);
      }
    });

    return normalizedBody as B;
  }

  getToken() {
    return localStorage.getItem(AccessToken);
  }

  clearToken() {
    localStorage.removeItem(AccessToken);
    localStorage.removeItem(RefreshToken);
  }

  buildUrlFromSegments(segments: Array<string>, version: number = 1) {
    let url = `v${version}`;
    for (const segment of segments) {
      url += `/${segment}`;
    }
    return url;
  }

  redirectAccessDenied(): Promise<boolean> {
    return this.router.navigate(["/access-denied"], { replaceUrl: true });
  }
}
